import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    CONDITION_ACTION, CONDITION_AMOUNT_TYPE, CONDITION_ANY_SPECIFIC,
    CONDITION_BEHAVIOR, CONDITION_MESSAGE, CONDITION_TIME_UNIT, CONDITION_TIMEFRAME,
    ConditionsProps,
    defaultConditions,
    segmentBuilderJourney
} from "../../../utils/segmentBuilder";
import SegmentBuilderStep from "../SegmentBuilderStep";
import "./new-segments.css";
import {getShopProducts} from "../../../api/getShopProducts";
import useUserContext from "../../../hooks/useUserContext";
import useAppContext from "../../../hooks/useAppContext";
import {Button} from "antd";
import {PlusOutlined} from "@ant-design/icons";
import SegmentBuilderHint from "../SegmentBuilderHint";
import {getSegmentShopClients} from "../../../api/getSegmentShopClients";
import {useDebouncedEffect} from "../../../hooks/useDebouncedEffect";
import {countryCodesList} from "../../../utils/countryCodesList";
import {TrashIcon} from "../../../assets/icons/TrashIcon";

const NewSegment = ({
    newSegmentSession,
    setIsValid,
    updateCustomersCount,
    setIsLoadingCount,
    segmentState,
    setSegmentState,
}: {
    newSegmentSession: number;
    setIsValid: (isValid: boolean) => void;
    updateCustomersCount: (newCount: number) => void;
    setIsLoadingCount: (newVal: boolean) => void;
    segmentState: ConditionsProps[];
    setSegmentState: (newState: ConditionsProps[]) => void;
}) => {
    const { selectedDomain } = useUserContext();
    const { data: { campaigns }} = useAppContext();
    const [segment, setSegment] = useState<any>({});
    const [productVariants, setProductVariants] = useState<any[]>([]);
    const [editIndex, setEditIndex] = useState<number>(0);
    const [errors, setErrors] = useState<Map<number, string | null>>(new Map());
    const controllerRef = useRef<AbortController | null>(null);

    const sentCampaigns = useMemo(() =>
        campaigns?.data?.filter((campaign: any) => campaign.state === 'sent' || campaign.state === 'partially sent')?.sort((a: any, b: any) => a.createdAt < b.createdAt)
    , [campaigns]);

    const countries = useMemo(() =>
        countryCodesList.map((country) => ({ label: country.countryName, value: country.country }))
    , [countryCodesList]);

    useEffect(() => {
        setIsValid(!errors.size);
    }, [errors]);

    const loadProducts = async () => {
        if(!selectedDomain) return;
        const newProductVariants: any[] = [];
        const response = await getShopProducts(selectedDomain);
        response?.data?.response?.forEach((product: any) => {
            product.variants.forEach((variant: any) => {
                newProductVariants.push({ key: `${product.id}-${variant.id}`, productId: product.id, variantId: variant.id, src: product.image.src, title: `${product.title} - ${variant.title}`, price: parseFloat(variant.price).toLocaleString(), currency: variant.currency });
            });
        });
        setProductVariants(newProductVariants);
    }

    const loadClientsCount = async () => {
        if (controllerRef.current) {
            controllerRef.current.abort();
        }
        const controller = new AbortController();
        controllerRef.current = controller;
        setIsLoadingCount(true);
        const response = selectedDomain ? await getSegmentShopClients(selectedDomain, segmentState, controller.signal) : null;
        response?.data?.length !== undefined && updateCustomersCount(response?.data?.length);
        if(response?.status !== -1) {
            setIsLoadingCount(false);
        }
    }

    const updateSegmentConditions = (index: number) => (key: string, value: any) => {
        const newSegmentState = segmentState.map((segment, i) => {
            if(index === i) {
                const newSegment: ConditionsProps = {};
                let leaf = segmentBuilderJourney;
                let count = 0;
                while(leaf && count < 40) {
                    count++;
                    const leafKey: keyof ConditionsProps = leaf.key;
                    const val: any = key === leafKey ? value : segment[leafKey] || defaultConditions[leafKey];
                    newSegment[leafKey] = val;

                    if(leaf.options) {
                        const nextLeaf = leaf.options?.find((option: any) => option.key === val)?.next;
                        if(nextLeaf) {
                            leaf = nextLeaf;
                        } else if(leaf.list) {
                            leaf = leaf.next;
                        }
                    } else if(leaf.input) {
                        leaf = leaf.next;
                    }
                }
                return newSegment;
            } else return segment;
        });
        setSegmentState(newSegmentState);
    }

    const addCondition = () => {
        setSegmentState([...segmentState, defaultConditions]);
    }

    const isInEnum = <T extends Record<string, string | number>>(value: any, enumObj: T): boolean => {
        return Object.values(enumObj).includes(value);
    }

    const validateCondition = (leafKey: string, value: any) => {
        if(leafKey === 'behavior' && !isInEnum(value, CONDITION_BEHAVIOR)) {
            return 'Select whether the user did or did not perform an action.';
        }
        else if(leafKey === 'action' && !isInEnum(value, CONDITION_ACTION)) {
            return 'Choose the action you want to filter by (e.g. purchased, abandoned, clicked).';
        } else if(leafKey === 'message' && !isInEnum(value, CONDITION_MESSAGE)) {
            return 'Select the type of message to filter (e.g. recovery, campaign, or event).';
        } else if(leafKey === 'campaign' && !isInEnum(value, CONDITION_ANY_SPECIFIC)) {
            return 'Select which campaign to include in the filter, or choose all campaigns.';
        } else if(leafKey === 'productType' && !isInEnum(value, CONDITION_ANY_SPECIFIC)) {
            return 'Choose a specific product or filter by all products combined.';
        } else if(leafKey === 'product' && !value) {
            return 'Choose a specific product or filter by all products combined.';
        } else if(leafKey === 'amountType' && !isInEnum(value, CONDITION_AMOUNT_TYPE)) {
            return 'Select how you want to filter by amount — exactly, over, or below.';
        } else if(leafKey === 'amount' && (value === '' || !value)) {
            return 'Enter the amount to use in the filter.';
        } else if(leafKey === 'priceType' && !isInEnum(value, CONDITION_AMOUNT_TYPE)) {
            return 'Select how you want to filter by amount — exactly, over, or below.';
        } else if(leafKey === 'price' && (value === '' || !value)) {
            return 'Enter the amount to use in the filter.';
        } else if(leafKey === 'timeframe' && !isInEnum(value, CONDITION_TIMEFRAME)) {
            return 'Select when the action happened — anytime or within a specific timeframe.';
        } else if(leafKey === 'sometimeLast' && (value === '' || !value)) {
            return 'Enter the number of days, weeks, or months for the timeframe.';
        } else if(leafKey === 'timeUnit' && !isInEnum(value, CONDITION_TIME_UNIT)) {
            return 'Select whether the time range is in days, weeks, months, or years.';
        } else if((leafKey === 'from' || leafKey === 'to') && (value === '' || !value)) {
            return 'Enter the dates range for the timeframe.';
        }
        return '';
    }

    const isValid = () => {
        const newErrors = new Map<number, string | null>();
        segmentState.forEach((conditions, index) => {
            let leaf = segmentBuilderJourney;
            let count = 0;
            let error: string | null = null;
            while(leaf && count < 40 && !error) {
                count++;
                const leafKey: keyof ConditionsProps = leaf.key;
                const val: any = conditions[leafKey];
                error = validateCondition(leafKey, val);

                if(leaf.options) {
                    leaf = leaf.options?.find((option: any) => option.key === val)?.next;
                } else if(leaf.input) {
                    leaf = leaf.next;
                }
            }

            error && newErrors.set(index, error);
            return error;
        });
        setErrors(newErrors);
    }

    useEffect(() => {
        loadProducts();
    }, []);

    // useEffect(() => {
    //     setSegmentState([defaultConditions]);
    // }, [newSegmentSession]);

    useEffect(() => {
        isValid();
    }, [segmentState]);

    useDebouncedEffect(() => {
        if(errors.size) return;
        loadClientsCount();
    }, [errors, segmentState], 500);

    const getExcludedActions = (index: number): string[] => {
        const excludedActions: string[] = [];
        segmentState.map((segment, i) => {
            if(i < index && segment.action) {
                excludedActions.push(segment.action);
            }
        });
        return excludedActions;
    }

    const removeCondition = (index: number) => () => {
        if(segmentState.length > 1) {
            const newSegmentState = segmentState.filter((conditions, i) => index !== i);
            setSegmentState(newSegmentState);
        }
    }

    return (
        <div>
            <div className="new-segment-flow">
                {segmentState.map((conditions, index) =>
                    <div key={`segment-condition-${conditions.behavior}-${conditions.action}-${index}`} className="segment-part-container flex" onClick={() => setEditIndex(index)}>
                        {index ? <div className="and-connector">And also filter by</div> : null}
                        <div className="segment-part flex">
                            <SegmentBuilderStep index={index} excludedActions={[]} sentCampaigns={sentCampaigns} countries={countries} productVariants={productVariants} conditions={conditions} onUpdate={updateSegmentConditions(index)} leaf={segmentBuilderJourney} onSelect={(key: string, newVal: any) => setSegment({...segment, [key]: newVal })} isEditMode={editIndex === index} />
                        </div>
                        <div className={`segment-part-right ${errors.get(index) ? 'warning' : 'approved'}`}>
                            <SegmentBuilderHint error={errors.get(index) || ''} conditions={conditions} isEditMode={editIndex === index} />
                            {segmentState.length > 1 ?
                                <div className="segment-part-actions">
                                    <Button size="middle" icon={<TrashIcon width={18} />} onClick={removeCondition(index)} >Remove</Button>
                                </div>
                            : null}
                        </div>
                    </div>
                )}
                <Button className="new-condition" icon={<PlusOutlined />} onClick={addCondition}>Add condition</Button>
            </div>
        </div>
    );
}

export default NewSegment;